import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import QRCode from 'react-qr-code';
import api from '../services/api';

function PrintLabels() {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [units, setUnits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingUnits, setLoadingUnits] = useState(false);

    // Modes: 'PRODUCT' or 'UNITS'
    const [mode, setMode] = useState('PRODUCT');

    // For Product Mode
    const [copies, setCopies] = useState(24); // 3x8 grid fit
    const [customBatch, setCustomBatch] = useState('');

    // For Unit Mode
    const [filters, setFilters] = useState({
        batch: '',
        status: 'available'
    });

    useEffect(() => {
        loadProduct();
    }, [id]);

    useEffect(() => {
        if (product) {
            setCustomBatch(product.batch_number || '');
            if (product.serialized) {
                setMode('UNITS'); // Default to units if serialized
                fetchUnits();
            }
        }
    }, [product]);

    useEffect(() => {
        if (mode === 'UNITS' && product?.serialized) {
            fetchUnits();
        }
    }, [filters]);

    const loadProduct = async () => {
        try {
            const response = await api.get(`/products/${id}`);
            setProduct(response.data.product || response.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUnits = async () => {
        setLoadingUnits(true);
        try {
            // Fetch all units (no pagination for printing usually, or high limit)
            const queryParams = new URLSearchParams({
                limit: 100 // Reasonable max for a print job
            });
            if (filters.status) queryParams.append('status', filters.status);
            // Note: Server API needs to support filtering by batch if we want that
            // If not supported, we filter locally

            const response = await api.get(`/products/${id}/units?${queryParams.toString()}`);
            let data = response.units || response.data?.units || response.data || [];

            if (filters.batch) {
                data = data.filter(u => (u.batch_number || '').includes(filters.batch));
            }

            setUnits(data);
        } catch (err) {
            console.error("Error fetching units for print", err);
        } finally {
            setLoadingUnits(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading...</div>;
    if (!product) return <div className="p-8 text-center text-rose-500">Product not found</div>;

    const publicProductUrl = `${window.location.origin}/p/${product.slug || product.id}`;

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
            {/* Control Panel - Hide on print */}
            <div className="bg-white border-b border-slate-200 p-4 shadow-sm print:hidden sticky top-0 z-10">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <Link to={`/products/${id}`} className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg bg-slate-50">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <div>
                            <h1 className="text-lg font-bold text-slate-800">Print Labels: {product.name}</h1>
                            <p className="text-xs text-slate-500">{product.sku}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 bg-slate-50 p-1 rounded-xl border border-slate-200">
                        {product.serialized && (
                            <button
                                onClick={() => setMode('UNITS')}
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${mode === 'UNITS' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                Unit Serial QRs
                            </button>
                        )}
                        <button
                            onClick={() => setMode('PRODUCT')}
                            className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${mode === 'PRODUCT' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                        >
                            Product Batch QR
                        </button>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={handlePrint}
                            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                            Print Labels
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="max-w-7xl mx-auto mt-4 pt-4 border-t border-slate-100 flex gap-4">
                    {mode === 'PRODUCT' && (
                        <>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Copies</label>
                                <input
                                    type="number"
                                    value={copies}
                                    onChange={(e) => setCopies(parseInt(e.target.value) || 1)}
                                    className="w-24 px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                                />
                            </div>
                            <div className="flex flex-col gap-1 flex-1 max-w-xs">
                                <label className="text-xs font-bold text-slate-500 uppercase">Batch Label</label>
                                <input
                                    type="text"
                                    value={customBatch}
                                    onChange={(e) => setCustomBatch(e.target.value)}
                                    placeholder="Enter batch number..."
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                                />
                            </div>
                        </>
                    )}

                    {mode === 'UNITS' && (
                        <>
                            <div className="flex flex-col gap-1 flex-1 max-w-xs">
                                <label className="text-xs font-bold text-slate-500 uppercase">Filter Batch</label>
                                <input
                                    type="text"
                                    value={filters.batch}
                                    onChange={(e) => setFilters(prev => ({ ...prev, batch: e.target.value }))}
                                    placeholder="Search batch..."
                                    className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs font-bold text-slate-500 uppercase">Status</label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                    className="w-32 px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                                >
                                    <option value="">All</option>
                                    <option value="available">Available</option>
                                    <option value="sold">Sold</option>
                                </select>
                            </div>
                            <div className="ml-auto text-sm text-slate-500 self-end">
                                Rendering {units.length} labels
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Print Preview Area - A4 Simulation */}
            <div className="flex-1 p-8 overflow-auto bg-slate-100 print:bg-white print:p-0">
                <div className="mx-auto bg-white shadow-2xl print:shadow-none min-h-[297mm] w-[210mm] p-[10mm] grid grid-cols-3 gap-4 content-start relative">
                    {/* Add print specific styles for A4 */}
                    <style>{`
                        @media print {
                            @page {
                                size: A4;
                                margin: 0;
                            }
                            body {
                                -webkit-print-color-adjust: exact;
                                background: white;
                            }
                            .print-break-inside-avoid {
                                break-inside: avoid;
                            }
                        }
                    `}</style>

                    {mode === 'PRODUCT' && (
                        Array.from({ length: copies }).map((_, idx) => (
                            <div key={idx} className="border border-slate-200 rounded-lg p-2 flex flex-col items-center justify-center text-center h-[40mm] print-break-inside-avoid">
                                <div className="w-16 h-16 bg-white mb-2">
                                    <QRCode value={publicProductUrl} size={64} style={{ height: "100%", width: "100%" }} />
                                </div>
                                <div className="text-[10px] uppercase font-bold text-slate-800 leading-tight w-full truncate px-1">
                                    {product.name}
                                </div>
                                {customBatch && (
                                    <div className="text-[9px] text-slate-500 font-mono mt-0.5">
                                        Batch: {customBatch}
                                    </div>
                                )}
                            </div>
                        ))
                    )}

                    {mode === 'UNITS' && (
                        units.map((unit) => (
                            <div key={unit.id} className="border border-slate-200 rounded-lg p-2 flex flex-col items-center justify-center text-center h-[40mm] print-break-inside-avoid">
                                <div className="w-16 h-16 bg-white mb-2">
                                    <QRCode value={`${window.location.origin}/units/${unit.id}`} size={64} style={{ height: "100%", width: "100%" }} />
                                </div>
                                <div className="text-[10px] uppercase font-bold text-slate-800 leading-tight w-full truncate px-1">
                                    {product.name}
                                </div>
                                <div className="text-[10px] text-slate-800 font-mono mt-0.5 font-bold">
                                    {unit.primary_identifier}
                                </div>
                                {unit.batch_number && (
                                    <div className="text-[8px] text-slate-500 font-mono mt-0.5">
                                        {unit.batch_number}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default PrintLabels;
